/**
 * The job card's assistant, as the API and the web both see it.
 *
 * A tool is one of three tiers, and the tier is the whole safety story:
 *
 * - `read` runs as soon as the model asks, and changes nothing.
 * - `immediate` writes straight away — a note, a follow-up, a person — and
 *   leaves a receipt with Undo. Nothing in this tier reaches a customer,
 *   books a visit or touches money.
 * - `approve` waits on a person's click: anything that goes to a customer,
 *   books or moves a visit, changes the stage, or prices, issues or sends a
 *   document. The database refuses an `EXECUTED` row with `requiresApproval`
 *   and no approver (decision 9).
 *
 * Every tool runs an ordinary procedure as the person asking, so the
 * permission checks, the locks and the send rules are the buttons' own.
 * Decision 94.
 */

export const ASSISTANT_TIERS = ['read', 'immediate', 'approve'] as const
export type AssistantTier = (typeof ASSISTANT_TIERS)[number]

export type AssistantToolStatus = 'PROPOSED' | 'AWAITING_APPROVAL' | 'EXECUTED' | 'REJECTED' | 'FAILED'

export type AssistantCardKind =
  | 'email'
  | 'document'
  | 'booking'
  | 'lines'
  | 'stage'
  | 'proposal'
  | 'invoice'
  | 'sms'
  | 'note'
  | 'task'
  | 'contact'
  | 'service'
  | 'brief'
  | 'job'
  | 'client'
  | 'agreement'
  | 'report'

/** One line of a price, as a card draws it. Integer cents inc GST. */
export interface AssistantCardLine {
  description: string
  quantity: string
  unitPriceIncTax: number
  totalIncTax: number
}

/**
 * What a card shows. Drawn from the tool's arguments when it is proposed, so
 * the person approves exactly the thing that will run.
 */
export interface AssistantCard {
  kind: AssistantCardKind
  title: string
  subtitle?: string | null
  body?: string | null
  fields?: Array<{ label: string; value: string }>
  lines?: AssistantCardLine[]
  totalIncTax?: number | null
  attachments?: Array<{ id: string; name: string }>
  /** The records the words drew on — the mockup's "Used" chips. */
  sources?: string[]
  /** Said above the buttons, when approving would be refused or surprising. */
  warning?: string | null
  /** For Edit in composer: the email exactly as it would go. */
  email?: {
    to: string[]
    cc: string[]
    bcc: string[]
    subject: string
    body: string
    attachmentIds: string[]
  } | null
  /** For an SMS draft: sent from the server when Telnyx is set up, else the phone's own compose window. */
  sms?: {
    to: string | null
    body: string
    /** `server` sends through Telnyx on Approve; `phone` opens the phone's own window (decision 116). */
    via?: 'server' | 'phone'
  } | null
  /** Which job the card acts on, when it is Eco's: a conversation that is not about one job. */
  job?: { id: string; number: string; client: string } | null
}

export interface AssistantToolCallView {
  id: string
  /** The assistant turn that asked for it. */
  turnId: string | null
  toolName: string
  tier: AssistantTier
  status: AssistantToolStatus
  /** A step's words while it runs, and a receipt's afterwards. */
  label: string
  card: AssistantCard | null
  error: string | null
  rejectedReason: string | null
  approvedByName: string | null
  /** Whether Undo is still offered. */
  undoable: boolean
  undone: boolean
  /** A picture of what a reading tool found: a day, a history, free times. */
  display: AssistantDisplay | null
  /** The arguments a person may change on the card before Approve. */
  editable: string[]
  /** Those arguments' values as proposed, for the card's Change form. */
  draft: Record<string, unknown> | null
}

export interface AssistantTurnView {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  occurredAt: Date
  toolCalls: AssistantToolCallView[]
  /** The person's thumbs on a reply: 1, -1 or null (decision 116, 2026-11-01). */
  rating: 1 | -1 | null
  ratingNote: string | null
}

export interface AssistantSessionView {
  id: string
  /** The job a job-card conversation is about; null for Eco, which is about the whole business. */
  jobId: string | null
  kind: AssistantKind
  startedAt: Date
  turns: AssistantTurnView[]
}

/**
 * `job` is the job card's assistant (decision 94): one job, the session's.
 * `eco` is the one in the command bar (decision 116): the whole business,
 * its tools taking the job or client they act on. `mcp` is an outside AI
 * app connected over MCP, with the same tools as Eco.
 */
export type AssistantKind = 'job' | 'eco' | 'mcp'

/** What the person is looking at when they talk to Eco, so "this job" means something. */
export interface AssistantFocus {
  jobId?: string | null
  clientId?: string | null
  agreementId?: string | null
  /** The screen, as a path: `/board`, `/clients/…`. */
  path?: string | null
}

/** What `POST /assistant/run` accepts. */
export type AssistantRunRequest =
  | { sessionId: string; action: 'summary'; focus?: AssistantFocus }
  | { sessionId: string; action: 'message'; text: string; focus?: AssistantFocus }
  | { sessionId: string; action: 'approve'; toolCallId: string; edits?: Record<string, unknown>; focus?: AssistantFocus }
  | { sessionId: string; action: 'reject'; toolCallId: string; reason?: string; resume?: boolean; focus?: AssistantFocus }

/**
 * One line of the NDJSON stream. `text` is the reply arriving; `toolCall` is
 * a step or a card, sent again whenever its status moves; `done` carries the
 * turns as saved, which replace whatever was drawn from the stream.
 */
export type AssistantStreamEvent =
  | { type: 'turn'; turn: AssistantTurnView }
  | { type: 'text'; delta: string }
  | { type: 'toolCall'; toolCall: AssistantToolCallView }
  | { type: 'done'; session: AssistantSessionView }
  | { type: 'error'; message: string }

/** The chips under the conversation. Plain prompts, sent as if typed. */
export const ASSISTANT_SUGGESTIONS = [
  { label: 'Draft technician brief', prompt: 'Draft a brief for the technician going to this job: what to do, access, hazards and anything the customer asked for.' },
  { label: 'Booking confirmation SMS', prompt: 'Draft an SMS to the job contact confirming the next booking.' },
  { label: 'Suggest a time', prompt: 'Suggest a few times in the next week to book this job, and who could go.' },
  { label: 'Create follow-up task', prompt: 'Create a follow-up task for the most useful next step on this job.' },
  { label: 'Summarise site history', prompt: 'Summarise the history of work at this property.' },
] as const

/* -------------------------------------------------------------------------- */
/* Eco's pictures                                                             */
/* -------------------------------------------------------------------------- */

/**
 * A reading tool's answer, drawn. The model is told the same facts in words;
 * this is what the person sees. A `pick` is a prompt sent as if typed when
 * the row is chosen, so choosing a time or a client is one tap and still a
 * plain message in the conversation. Decision 116.
 */
export type AssistantDisplay =
  | AssistantDayDisplay
  | AssistantSlotsDisplay
  | AssistantHistoryDisplay
  | AssistantPicksDisplay
  | AssistantJobsDisplay
  | AssistantAgreementDisplay
  | AssistantClientDisplay

export interface AssistantStat {
  label: string
  value: string
  /** `good` is Eco Green, `problem` the one red; anything else is ink. */
  tone?: 'good' | 'problem' | null
}

export interface AssistantDayDisplay {
  kind: 'day'
  /** YYYY-MM-DD, the business's day. */
  date: string
  title: string
  stats: AssistantStat[]
  /** Bookings per person: done and still to go. */
  people: Array<{ name: string; done: number; booked: number }>
  /** The day's bookings in time order. */
  visits: Array<{
    jobId: string | null
    start: string
    end: string
    title: string
    subtitle: string | null
    people: string[]
    /** `past` has gone by without being checked off; `missed` was marked a no-show. */
    state: 'done' | 'live' | 'coming' | 'past' | 'missed' | 'cancelled'
  }>
  /** Other things that happened: quotes, payments, new work. */
  events: Array<{ label: string; detail: string; jobId: string | null }>
}

export interface AssistantSlotsDisplay {
  kind: 'slots'
  title: string
  minutes: number
  jobId: string | null
  days: Array<{
    date: string
    label: string
    slots: Array<{ start: string; end: string; userId: string; person: string; pick: string }>
  }>
}

export interface AssistantHistoryDisplay {
  kind: 'history'
  title: string
  clientId: string
  /** Visits a year, oldest first, for the bar chart. */
  years: Array<{ year: string; visits: number }>
  rows: Array<{
    jobId: string
    date: string | null
    number: string
    what: string
    where: string | null
    people: string[]
    outcome: string
    amountIncTax: number | null
  }>
}

/** A short list to choose from: clients that match, agreements coming due. */
export interface AssistantPicksDisplay {
  kind: 'picks'
  title: string
  rows: Array<{ id: string; title: string; detail: string | null; pick: string }>
}

export interface AssistantJobsDisplay {
  kind: 'jobs'
  title: string
  stats?: AssistantStat[]
  rows: Array<{
    jobId: string
    number: string
    client: string
    detail: string | null
    stage: string
    when: string | null
    amountIncTax: number | null
  }>
}

export interface AssistantAgreementDisplay {
  kind: 'agreement'
  agreementId: string
  title: string
  client: string
  status: string
  term: string
  stats: AssistantStat[]
  /** The term's visits, for the strip: done, booked, planned, skipped, late. */
  visits: Array<{
    scheduleId: string
    sequence: number
    dueOn: string
    service: string
    site: string
    state: 'done' | 'booked' | 'planned' | 'skipped' | 'late'
    jobId: string | null
  }>
  next: { dueOn: string; service: string | null; site: string; late: boolean } | null
}

export interface AssistantClientDisplay {
  kind: 'client'
  clientId: string
  title: string
  stats: AssistantStat[]
  people: Array<{ name: string; detail: string | null }>
  sites: Array<{ name: string; detail: string | null }>
}

/** What the assistant says when the person presses Stop. */
export const ASSISTANT_STOPPED = 'Stopped. Tell me what to do next.'

/** What a thumbs-down can say was wrong, in the person's words. */
export const ECO_THUMBS_DOWN = ['Wrong answer', 'Didn’t do what I asked', 'Too long', 'Something broke'] as const

/** The chips for an empty conversation with a job open. */
export const ECO_JOB_SUGGESTIONS = [
  { label: 'Summarise this job', prompt: 'Summarise this job: where it is, what has happened, and what is next.' },
  { label: 'Text the customer', prompt: 'Draft a text to this job’s contact about the next step.' },
  { label: 'Find a time', prompt: 'Find a few times to book this job in the next week.' },
  { label: 'Site history', prompt: 'Show me the history of work at this property.' },
] as const

/** The chips for an empty conversation with a client open. */
export const ECO_CLIENT_SUGGESTIONS = [
  { label: 'Their history', prompt: 'Show me this client’s booking history.' },
  { label: 'Agreements', prompt: 'What agreements does this client have, and when is the next service?' },
  { label: 'Email them', prompt: 'Draft an email to this client.' },
  { label: 'Book a job', prompt: 'I want to book a job for this client.' },
] as const

/** The chips under Eco's conversation, for an empty one. */
export const ECO_SUGGESTIONS = [
  { label: 'What happened today', prompt: 'What has happened today? Show me the day.' },
  { label: 'Yesterday', prompt: 'Give me a rundown of yesterday.' },
  { label: 'Book a job', prompt: 'I want to book a job.' },
  { label: 'Needs attention', prompt: 'What needs my attention right now?' },
  { label: 'Next agreement services', prompt: 'Which service agreement visits are due in the next two weeks?' },
] as const
